import React, { useState, useRef } from 'react';
import { format } from 'date-fns';
import { Eye, EyeOff } from 'lucide-react';

// Star rating component
const StarRating = ({ rating }) => {
  const stars = [];
  
  for (let i = 1; i <= 5; i++) {
    if (i <= rating) {
      // Full star
      stars.push(
        <svg key={i} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      );
    } else {
      // Empty star
      stars.push(
        <svg key={i} className="w-4 h-4 text-gray-300" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      );
    }
  }
  
  return <div className="flex">{stars}</div>;
};

// User Initial Circle component
const UserInitialCircle = ({ name }) => {
  // Get the first letter of the name
  const initial = name ? name.charAt(0).toUpperCase() : 'U';
  
  // Generate a consistent color based on the name
  const getColorClass = (name) => {
    const colors = [
      'bg-blue-500', 'bg-purple-500', 'bg-green-500', 
      'bg-red-500', 'bg-indigo-500', 'bg-pink-500',
      'bg-yellow-500', 'bg-teal-500'
    ];
    
    // Simple hash function to pick a color consistently for the same name
    const hash = name ? name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) : 0;
    return colors[hash % colors.length];
  };
  
  return (
    <div className={`h-8 w-8 rounded-full ${getColorClass(name)} flex items-center justify-center text-white font-medium text-sm`}>
      {initial}
    </div>
  );
};

// Source Icon component for NPS
const SourceIcon = ({ source }) => {
  return (
    <div className="h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 6V18M18 12H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </div>
  );
};

// Sentiment badge component for NPS data
const SentimentBadge = ({ sentiment }) => {
  let bgColor = 'bg-gray-100 text-gray-800';
  
  if (sentiment === 'Positive') {
    bgColor = 'bg-green-100 text-green-800';
  } else if (sentiment === 'Negative') {
    bgColor = 'bg-red-100 text-red-800';
  } else if (sentiment === 'Neutral') {
    bgColor = 'bg-blue-100 text-blue-800';
  }
  
  const npsLabel = 
    sentiment === 'Positive' ? 'Promoter' :
    sentiment === 'Negative' ? 'Detractor' : 'Passive';
  
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bgColor}`}>
      {npsLabel}
    </span>
  );
};

// Reply status badge component
const ReplyStatusBadge = ({ status }) => {
  const isReplied = status === 'Replied';
  const bgColor = isReplied ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800';
  
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bgColor}`}>
      {status}
    </span>
  );
};

// Product badge component
const ProductBadge = ({ product }) => {
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-purple-100 text-purple-800">
      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
      </svg>
      {product}
    </span>
  );
};

// Branch badge component
const BranchBadge = ({ branch }) => {
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
      {branch}
    </span>
  );
};

// Compact categories display
const CategoriesDisplay = ({ categoryMap }) => {
  const [expandedCategory, setExpandedCategory] = useState(null);

  const getSentimentColor = (sentiment) => {
    if (sentiment.toLowerCase() === 'positive') return 'bg-green-100 text-green-800 border-green-200';
    if (sentiment.toLowerCase() === 'negative') return 'bg-red-100 text-red-800 border-red-200';
    return 'bg-blue-100 text-blue-800 border-blue-200';
  };

  return (
    <div className="inline-flex flex-wrap gap-1 items-center">
      {Object.entries(categoryMap).map(([category, data], index) => {
        const displayCategory = category.replace('_', ' ');
        const formattedCategory = displayCategory.charAt(0).toUpperCase() + displayCategory.slice(1);
        const sentimentColor = getSentimentColor(data.sentiment);
        const hasSubcategories = data.subcategories && data.subcategories.length > 0;
        
        return (
          <div key={index} className="relative inline-flex">
            <div 
              className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded ${sentimentColor} ${hasSubcategories ? 'cursor-pointer pr-5' : ''}`}
              onClick={() => hasSubcategories ? setExpandedCategory(expandedCategory === category ? null : category) : null}
            >
              <svg className="w-3 h-3 mr-1 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              {formattedCategory}
              {hasSubcategories && (
                <svg 
                  className={`w-3 h-3 ml-0.5 transition-transform ${expandedCategory === category ? 'transform rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              )}
            </div>
            
            {/* Dropdown for subcategories */}
            {hasSubcategories && expandedCategory === category && (
              <div className="absolute top-full left-0 mt-1 z-10 bg-white shadow-md rounded-md p-1 border border-gray-200 min-w-max">
                <div className="max-h-24 overflow-y-auto">
                  {data.subcategories.map((subcat, idx) => (
                    <div key={idx} className="px-2 py-1 text-xs hover:bg-gray-50 rounded whitespace-nowrap">
                      {subcat}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const ReviewCard = ({ review }) => {
  const [showReply, setShowReply] = useState(false);
  const [isNameMasked, setIsNameMasked] = useState(true);
  const [showPinPopover, setShowPinPopover] = useState(false);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState(false);
  const pinPopoverRef = useRef(null);
  const pinInputRef = useRef(null);
  
  // Format date for display
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return format(date, 'MMM d, yyyy');
    } catch (error) {
      return dateString;
    }
  };

  // Get a realistic name if none exists
  const getReviewerName = () => {
    // if (review.userName && review.userName.trim() !== '') {
    //   return review.userName;
    // }
    
    // List of realistic names to use when no name is provided
    const names = [
      'Aditya Sharma', 'Priya Patel', 'Raj Malhotra', 'Ananya Singh', 
      'Vikram Mehta', 'Nisha Kapoor', 'Sanjay Kumar', 'Meera Reddy',
      'Arjun Nair', 'Divya Choudhury', 'Rahul Verma', 'Sunita Gupta'
    ];
    
    // Use a hash of the review text or date to consistently pick the same name for the same review
    const hash = (review.reviewText?.length || 0) + (new Date(review.date || Date.now()).getDate() || 0);
    return names[hash % names.length];
  };

  // Process categories and subcategories
  const processCategories = () => {
    const categoryMap = {};
    
    // First priority: use processed_data.categories (this is the raw AI-analyzed data)
    if (review.processed_data && review.processed_data.categories) {
      review.processed_data.categories.forEach(cat => {
        const categoryName = cat.category;
        if (!categoryMap[categoryName]) {
          categoryMap[categoryName] = {
            sentiment: cat.sentiment,
            subcategories: []
          };
        }
      });
    }
    
    // Second priority: Group subcategories by their parent category
    if (review.processed_data && review.processed_data.subcategories) {
      review.processed_data.subcategories.forEach(subcat => {
        if (!categoryMap[subcat.category]) {
          categoryMap[subcat.category] = {
            sentiment: subcat.sentiment,
            subcategories: []
          };
        }
        categoryMap[subcat.category].subcategories.push(subcat.subcategory);
      });
    } 
    
    // Third priority: use the existing categorySentiments if available (fallback)
    if (Object.keys(categoryMap).length === 0 && review.categorySentiments) {
      const seenCategories = new Set();
      
      review.categorySentiments.forEach(cat => {
        if (!seenCategories.has(cat.category)) {
          seenCategories.add(cat.category);
          categoryMap[cat.category] = {
            sentiment: cat.sentiment,
            subcategories: []
          };
        }
      });
    }
    
    return categoryMap;
  };

  // Mask name function
  const getMaskedName = (name) => {
    if (!name) return 'Anonymous';
    if (!isNameMasked) return name;
    
    // Keep first character, mask the rest
    const firstChar = name.charAt(0);
    const maskedPart = '*'.repeat(Math.min(name.length - 1, 5));
    return `${firstChar}${maskedPart}`;
  };

  // Handle PIN submission
  const handlePinSubmit = (e) => {
    e.preventDefault();
    // Simple PIN validation - just check if length is 4
    if (pin.length === 4) {
      setIsNameMasked(false);
      setShowPinPopover(false);
      setPinError(false);
      setPin('');
    } else {
      setPinError(true);
    }
  };

  // Close popover when clicking outside
  const handleClickOutside = (e) => {
    if (pinPopoverRef.current && !pinPopoverRef.current.contains(e.target)) {
      setShowPinPopover(false);
    }
  };

  // Add event listener for outside clicks
  React.useEffect(() => {
    if (showPinPopover) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPinPopover]);

  // Focus on PIN input when popover appears
  React.useEffect(() => {
    if (showPinPopover && pinInputRef.current) {
      // Short timeout to ensure the input is rendered
      setTimeout(() => {
        pinInputRef.current.focus();
      }, 50);
    }
  }, [showPinPopover]);

  const categoryMap = processCategories();
  const reviewerName = getReviewerName();

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 shadow-sm hover:shadow-md transition-shadow duration-200">
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center">
          <UserInitialCircle name={reviewerName} />
          <div className="ml-2 relative">
            <div className="flex items-center">
              <h3 className="font-medium">{getMaskedName(reviewerName)}</h3>
              <button 
                onClick={() => isNameMasked ? setShowPinPopover(true) : setIsNameMasked(true)}
                className="ml-2 text-xs"
                aria-label={isNameMasked ? "Unmask name" : "Mask name"}
              >
                {isNameMasked ? (
                  <Eye size={14} className="text-blue-600 hover:text-blue-800" />
                ) : (
                  <EyeOff size={14} className="text-gray-600 hover:text-gray-800" />
                )}
              </button>
            </div>
            <div className="text-xs text-gray-500 flex items-center">
              <span>{formatDate(review.date)}</span>
              <span className="mx-1">•</span>
              <span>{review.region}, {review.city}</span>
            </div>

            {/* PIN Popover */}
            {showPinPopover && (
              <div 
                ref={pinPopoverRef}
                className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg p-3 z-20 w-48"
              >
                <form onSubmit={handlePinSubmit}>
                  <div className="mb-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Enter 4-digit PIN to unmask
                    </label>
                    <input
                      ref={pinInputRef}
                      type="password"
                      maxLength="4"
                      className={`w-full px-2 py-1 text-sm border ${pinError ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500`}
                      value={pin}
                      onChange={(e) => {
                        const newPin = e.target.value.replace(/[^0-9]/g, '');
                        setPinError(false);
                        setPin(newPin);
                      }}
                      placeholder="••••"
                    />
                    {pinError && (
                      <p className="text-xs text-red-500 mt-1">Invalid PIN</p>
                    )}
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => setShowPinPopover(false)}
                      className="mr-2 px-2 py-1 text-xs text-gray-600 hover:text-gray-800"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-2 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Submit
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <StarRating rating={review.rating} />
          <div className="ml-2">
            <SentimentBadge sentiment={review.sentiment} />
          </div>
        </div>
      </div>
      
      <div className="mb-3">
        <p className="text-gray-700">{review.reviewText}</p>
      </div>
      
      {/* Info badges and categories in a single row */}
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs mb-3 pb-3 border-b border-gray-100">
        {/* Categories on the left */}
        <div className="flex flex-wrap items-center gap-2">
          {Object.keys(categoryMap).length > 0 && (
            <>
              <CategoriesDisplay categoryMap={categoryMap} />
            </>
          )}
        </div>
        
        {/* Product and Branch badges on the right */}
        <div className="flex flex-wrap items-center gap-2">
          {review.productOwnership && <ProductBadge product={review.productOwnership} />}
          {review.transactingBranch && <BranchBadge branch={review.transactingBranch} />}
        </div>
      </div>
      
      <div className="flex justify-between items-center">
        <div>
          <ReplyStatusBadge status={review.replyStatus} />
        </div>
        <div>
          {review.replyStatus === 'Replied' && (
            <button
              onClick={() => setShowReply(!showReply)}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              {showReply ? 'Hide Reply' : 'View Reply'}
            </button>
          )}
          {review.replyStatus === 'Not Replied' && (
            <button className="text-sm text-blue-600 hover:text-blue-800 flex items-center">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
              </svg>
              Reply
            </button>
          )}
        </div>
      </div>
      
      {showReply && review.replyText && (
        <div className="mt-3 bg-gray-50 p-3 rounded-md">
          <div className="flex items-center mb-2">
            <div className="h-6 w-6 rounded-full bg-blue-500 text-white flex items-center justify-center">
              <span className="text-xs font-medium">Bank</span>
            </div>
            <div className="ml-2">
              <p className="text-sm font-medium">Banking Support</p>
              <p className="text-xs text-gray-500">{formatDate(review.replyDate)}</p>
            </div>
          </div>
          <p className="text-sm text-gray-700">{review.replyText}</p>
        </div>
      )}
    </div>
  );
};

export default ReviewCard;